import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Mic, MicOff, RotateCcw, Speaker, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  createDrillSession,
  getDrillAudio,
  getDueFlashcards,
  reviewFlashcard,
  submitDrillAttempt,
  type DrillAttemptResult,
  type DrillItem,
  type Flashcard,
} from '../api/learning';
import { useDrillRecording } from '../hooks/useDrillRecording';
import { usePreloadedAudio } from '../hooks/usePreloadedAudio';
import { ArcadePersonalBestsPanel } from './ArcadePersonalBests';
import { SpeedMatchGrid } from './SpeedMatchGrid';
import { ListenPickGame } from './ListenPickGame';
import { SentenceScrambleGame } from './SentenceScrambleGame';
import './LearningPage.css';

type View = 'flashcards' | 'drills' | 'speed-match' | 'listen-pick' | 'sentence-scramble';

function ErrorNotice({ message }: { message: string }) {
  return (
    <div className="learning-notice learning-notice--error" role="alert">
      <AlertCircle aria-hidden="true" /> <span>{message}</span>
    </div>
  );
}

function FlashcardReview() {
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState<Flashcard | null>(null);

  useEffect(() => {
    getDueFlashcards()
      .then(setCards)
      .catch(() => setError('We could not load your flashcards.'))
      .finally(() => setLoading(false));
  }, []);

  const card = cards[0];

  async function submit(quality: 1 | 5) {
    if (!card) return;
    setSubmitting(true);
    setError(null);
    try {
      const serverCard = await reviewFlashcard(card.id, quality);
      setReviewed(serverCard);
      setCards(current => current.slice(1));
      setRevealed(false);
    } catch {
      setError('Your answer was not saved. Try again to keep your place.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p role="status">Loading your review…</p>;

  return (
    <section aria-labelledby="flashcard-heading">
      <div className="learning-section-heading">
        <div>
          <h2 id="flashcard-heading">Flashcard review</h2>
          <p>Recall the translation, then reveal the answer.</p>
        </div>
        {cards.length > 0 && <span className="learning-count">{cards.length} due</span>}
      </div>

      {error && <ErrorNotice message={error} />}
      {reviewed && (
        <div className="learning-notice learning-notice--success" role="status">
          <CheckCircle2 aria-hidden="true" />
          <span>
            Saved. Server scheduled the next review for{' '}
            {new Date(reviewed.nextReviewAt).toLocaleDateString()}.
          </span>
        </div>
      )}

      {!card ? (
        <div className="learning-empty">
          <CheckCircle2 aria-hidden="true" />
          <h3>You’re caught up</h3>
          <p>There are no cards due for review.</p>
        </div>
      ) : (
        <>
          <button
            className={`flashcard ${revealed ? 'flashcard--revealed' : ''}`}
            onClick={() => setRevealed(value => !value)}
            aria-label={revealed ? 'Hide flashcard answer' : 'Reveal flashcard answer'}
            aria-pressed={revealed}
          >
            <span className="flashcard-label">{revealed ? 'Translation' : 'Term'}</span>
            <span className="flashcard-text">{revealed ? card.back : card.front}</span>
            <span className="flashcard-hint">
              <RotateCcw size={18} aria-hidden="true" /> {revealed ? 'Show term' : 'Reveal answer'}
            </span>
          </button>

          {revealed && (
            <div className="review-actions" aria-label="Rate your answer">
              <button
                disabled={submitting}
                className="review-button review-button--incorrect"
                onClick={() => submit(1)}
              >
                <XCircle aria-hidden="true" /> Incorrect
              </button>
              <button
                disabled={submitting}
                className="review-button review-button--correct"
                onClick={() => submit(5)}
              >
                <CheckCircle2 aria-hidden="true" /> Correct
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function drillFeedbackMessage(result: DrillAttemptResult): string {
  if (result.verdict === 'correct') {
    return `Correct. We heard “${result.transcript}”.`;
  }
  if (result.feedbackCode === 'low-confidence') {
    return `We are not sure we heard that clearly. We heard “${result.transcript}”. Try again.`;
  }
  return `Not quite. We heard “${result.transcript}”. Try saying the phrase again.`;
}

function AudioDrills() {
  const [item, setItem] = useState<DrillItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<DrillAttemptResult | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const submitGuardRef = useRef(false);
  const itemRef = useRef<DrillItem | null>(null);
  itemRef.current = item;

  /** Single assessment path for manual stop and max-duration auto-stop. */
  const assessBlob = useCallback(async (blob: Blob | null) => {
    const current = itemRef.current;
    if (!current || submitGuardRef.current) return;
    submitGuardRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (!blob || blob.size === 0) {
        setError('We could not capture audio. Keep the phrase visible and try again.');
        setStatusMessage(null);
        return;
      }
      setStatusMessage('Checking your attempt…');
      const result = await submitDrillAttempt(current.id, blob);
      setFeedback(result);
      setStatusMessage(drillFeedbackMessage(result));
    } catch {
      setError('Assessment is unavailable right now. The phrase is still shown — try again.');
      setStatusMessage(null);
      setFeedback(null);
    } finally {
      setSubmitting(false);
      submitGuardRef.current = false;
    }
  }, []);

  const recording = useDrillRecording(undefined, {
    onAutoStop: blob => {
      void assessBlob(blob);
    },
  });

  useEffect(() => {
    createDrillSession()
      .then(session => setItem(session.currentItem))
      .catch(() => setError('We could not start an audio drill.'))
      .finally(() => setLoading(false));
  }, []);

  // Recording cleanup (mic tracks + MediaRecorder) runs inside useDrillRecording on unmount.

  const audio = usePreloadedAudio(item?.id ?? null, getDrillAudio);
  const audioError = 'Audio is unavailable. Use the visible text to continue this drill.';

  function play() {
    setError(null);
    void audio.play().catch(() => setError(audioError));
  }

  async function startRecording() {
    if (submitting || submitGuardRef.current || recording.isRecording) return;
    setError(null);
    setFeedback(null);
    setStatusMessage('Requesting microphone…');
    await recording.start();
    setStatusMessage(null);
  }

  async function stopAndAssess() {
    if (!item || submitting || submitGuardRef.current) return;
    setStatusMessage('Stopping recording…');
    const blob = await recording.stop();
    await assessBlob(blob);
  }

  function retryAttempt() {
    setFeedback(null);
    setError(null);
    setStatusMessage(null);
  }

  if (loading) return <p role="status">Preparing your audio drill…</p>;

  const micDenied = recording.phase === 'denied' || recording.errorCode === 'permission-denied';
  const micUnavailable = recording.phase === 'unavailable' || recording.errorCode === 'unavailable';
  const busy = submitting || recording.phase === 'requesting' || recording.phase === 'stopping';
  // Denial must not strand the exercise: Record stays enabled so the learner can
  // re-request permission after changing browser settings (copy: "then try again").
  const recordDisabled = busy;

  return (
    <section aria-labelledby="drill-heading">
      <div className="learning-section-heading">
        <div>
          <h2 id="drill-heading">Listen and repeat</h2>
          <p>Play the phrase, record your attempt, then get server feedback.</p>
        </div>
      </div>
      {(error || audio.loadFailed) && <ErrorNotice message={error ?? audioError} />}
      {micDenied && (
        <ErrorNotice message="Microphone access was denied. Enable the mic in browser settings, then try again. The phrase stays visible below." />
      )}
      {micUnavailable && !micDenied && (
        <ErrorNotice message="Microphone is unavailable on this device. Use the visible phrase to practice, then try again later." />
      )}
      {!item ? (
        <div className="learning-empty">
          <Speaker aria-hidden="true" />
          <h3>No drills yet</h3>
          <p>Save vocabulary to create a drill.</p>
        </div>
      ) : (
        <div className="drill-card">
          <span className="flashcard-label">Your phrase</span>
          <p className="drill-prompt">{item.prompt}</p>
          <p className="drill-cue" data-testid="drill-cue">
            Cue: {item.cueTerm}
          </p>
          <p
            className="drill-text"
            lang={item.language}
            data-testid="drill-expected-phrase"
            aria-label={`Speak: ${item.expectedPhrase}`}
          >
            {item.expectedPhrase}
          </p>
          <div className="drill-actions" role="group" aria-label="Listen and repeat controls">
            <button
              className="play-button"
              onClick={play}
              disabled={!audio.ready || audio.playing || recording.isRecording || busy}
              aria-label={`Play audio for ${item.expectedPhrase}`}
            >
              <Speaker aria-hidden="true" />{' '}
              {!audio.ready ? 'Loading audio…' : audio.playing ? 'Playing…' : 'Play phrase'}
            </button>
            {!recording.isRecording ? (
              <button
                type="button"
                className="record-button"
                onClick={() => void startRecording()}
                disabled={recordDisabled}
                aria-label="Start recording your attempt"
                aria-busy={recording.phase === 'requesting' || undefined}
              >
                <Mic aria-hidden="true" />{' '}
                {recording.phase === 'requesting' ? 'Requesting…' : 'Record'}
              </button>
            ) : (
              <button
                type="button"
                className="record-button record-button--active"
                onClick={() => void stopAndAssess()}
                disabled={busy}
                aria-label="Stop recording and check attempt"
                aria-pressed="true"
              >
                <MicOff aria-hidden="true" /> Stop &amp; check
              </button>
            )}
          </div>
          <p className="drill-caption">
            The spoken phrase is always shown above. Feedback checks the words you said, not accent
            quality.
          </p>
          {recording.isRecording && (
            <p className="drill-recording-indicator" role="status" aria-live="polite">
              <span className="drill-recording-dot" aria-hidden="true" /> Recording… (auto-stops)
            </p>
          )}
          {(statusMessage || feedback) && (
            <div
              className={`answer-feedback ${
                feedback?.verdict === 'correct'
                  ? 'answer-feedback--correct'
                  : feedback
                    ? 'answer-feedback--incorrect'
                    : ''
              }`}
              data-testid="drill-feedback"
            >
              {feedback?.verdict === 'correct' ? (
                <CheckCircle2 aria-hidden="true" />
              ) : feedback ? (
                <RotateCcw aria-hidden="true" />
              ) : null}
              <span
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="drill-feedback-message"
              >
                {statusMessage ?? (feedback ? drillFeedbackMessage(feedback) : null)}
              </span>
              {feedback?.verdict === 'retry' && (
                <button
                  type="button"
                  onClick={retryAttempt}
                  disabled={busy || recording.isRecording}
                >
                  Try again
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function LearningPage() {
  const [view, setView] = useState<View>('flashcards');
  return (
    <div className="learning-page container">
      <header className="learning-header">
        <span className="learning-eyebrow">Learning suite</span>
        <h1>Practice what you translate</h1>
        <p>Build recall with focused reviews, audio drills, and quick matching games.</p>
      </header>
      <ArcadePersonalBestsPanel />
      <div className="learning-tabs" role="tablist" aria-label="Learning activities">
        <button
          id="learning-tab-flashcards"
          role="tab"
          aria-selected={view === 'flashcards'}
          aria-controls="learning-panel"
          onClick={() => setView('flashcards')}
        >
          Flashcards
        </button>
        <button
          id="learning-tab-drills"
          role="tab"
          aria-selected={view === 'drills'}
          aria-controls="learning-panel"
          onClick={() => setView('drills')}
        >
          Audio drills
        </button>
        <button
          id="learning-tab-speed-match"
          role="tab"
          aria-selected={view === 'speed-match'}
          aria-controls="learning-panel"
          onClick={() => setView('speed-match')}
        >
          Speed Match
        </button>
        <button
          id="learning-tab-listen-pick"
          role="tab"
          aria-selected={view === 'listen-pick'}
          aria-controls="learning-panel"
          onClick={() => setView('listen-pick')}
        >
          Listen &amp; Pick
        </button>
        <button
          id="learning-tab-sentence-scramble"
          role="tab"
          aria-selected={view === 'sentence-scramble'}
          aria-controls="learning-panel"
          onClick={() => setView('sentence-scramble')}
        >
          Sentence Scramble
        </button>
      </div>
      <div className="learning-ghost-link">
        <Link to="/learning/rapid-fire">Rapid Fire</Link>
        <Link to="/learning/survival">Survival</Link>
        <Link to="/learning/ghost">Beat Your Ghost</Link>
      </div>
      <div
        id="learning-panel"
        className="learning-panel"
        role="tabpanel"
        aria-labelledby={`learning-tab-${view}`}
      >
        {view === 'flashcards' && <FlashcardReview />}
        {view === 'drills' && <AudioDrills />}
        {view === 'speed-match' && <SpeedMatchGrid />}
        {view === 'listen-pick' && <ListenPickGame />}
        {view === 'sentence-scramble' && <SentenceScrambleGame />}
      </div>
    </div>
  );
}
